import type { SectionComponent } from "./types";

// Section components with a large, rarely-used dependency graph — or, for the
// kit sections (ADR-0076), premium CSS + form logic that must not enter the
// shared catch-all bundle — are code-split into their own chunk via dynamic
// import() so the catch-all route entry (`@category/@page`) no longer bundles
// (or evaluates) them on every page. SSR is synchronous (`renderToString`) and
// cannot await a Suspense boundary, so the module is preloaded in +onRenderHtml
// (server) and +onRenderClient (client) before render/hydrate, then read
// synchronously from `resolved`.
//
// `moduleId` is the section chunk's STABLE (hash-free) key in Vike's build
// `assets.json` — the SSR head reads it to hoist this section's code-split CSS
// into a render-blocking <link> so a pure-JSON kit page (no full-page custom
// component — e.g. /resume, /interview, /, the guide lead magnets) paints
// styled instead of flashing unstyled. It MUST equal the dynamic import()'s
// resolved module file: the `@/...` specifier with `@` → `src` and the source
// extension (`.tsx`). Keep the two in lockstep — guarded by
// tests/unit/section-lazy-loaders.test.ts. See
// docs/specs/website/route-bundle-splitting.md "Per-page CSS hoist".
//
// `kit-markdown` pulls react-markdown + remark-gfm + the micromark/mdast/unified
// stack (~560 KB rendered) but is used by exactly one page config (docs).

type SectionLoader = () => Promise<SectionComponent>;

type SectionDef = {
  load: SectionLoader;
  moduleId: string;
};

const SECTIONS: Record<string, SectionDef> = {
  "kit-markdown": {
    load: () => import("@/components/kit/KitMarkdown/KitMarkdown").then((m) => m.KitMarkdown),
    moduleId: "src/components/kit/KitMarkdown/KitMarkdown.tsx",
  },
  "kit-hero-simple": {
    load: () => import("@/components/kit/KitHeroSimple/KitHeroSimple").then((m) => m.KitHeroSimple),
    moduleId: "src/components/kit/KitHeroSimple/KitHeroSimple.tsx",
  },
  "kit-quiz": {
    load: () => import("@/components/kit/KitQuiz/KitQuiz").then((m) => m.KitQuiz),
    moduleId: "src/components/kit/KitQuiz/KitQuiz.tsx",
  },
  "kit-takeaways": {
    load: () => import("@/components/kit/KitTakeaways/KitTakeaways").then((m) => m.KitTakeaways),
    moduleId: "src/components/kit/KitTakeaways/KitTakeaways.tsx",
  },
  "kit-trainer": {
    load: () => import("@/components/kit/KitTrainer/KitTrainer").then((m) => m.KitTrainer),
    moduleId: "src/components/kit/KitTrainer/KitTrainer.tsx",
  },
  "kit-about-tags": {
    load: () => import("@/components/kit/KitAboutTags/KitAboutTags").then((m) => m.KitAboutTags),
    moduleId: "src/components/kit/KitAboutTags/KitAboutTags.tsx",
  },
  "kit-navbar": {
    load: () => import("@/components/kit/KitNavbar/KitNavbar").then((m) => m.KitNavbar),
    moduleId: "src/components/kit/KitNavbar/KitNavbar.tsx",
  },
  "kit-footer": {
    load: () => import("@/components/kit/KitFooter/KitFooter").then((m) => m.KitFooter),
    moduleId: "src/components/kit/KitFooter/KitFooter.tsx",
  },
  "kit-guide-hero": {
    load: () => import("@/components/kit/KitGuideHero/KitGuideHero").then((m) => m.KitGuideHero),
    moduleId: "src/components/kit/KitGuideHero/KitGuideHero.tsx",
  },
  "kit-inside-showcase": {
    load: () =>
      import("@/components/kit/KitInsideShowcase/KitInsideShowcase").then(
        (m) => m.KitInsideShowcase,
      ),
    moduleId: "src/components/kit/KitInsideShowcase/KitInsideShowcase.tsx",
  },
  "kit-guide-services": {
    load: () =>
      import("@/components/kit/KitGuideServices/KitGuideServices").then((m) => m.KitGuideServices),
    moduleId: "src/components/kit/KitGuideServices/KitGuideServices.tsx",
  },
  "kit-faq": {
    load: () => import("@/components/kit/KitFaq/KitFaq").then((m) => m.KitFaq),
    moduleId: "src/components/kit/KitFaq/KitFaq.tsx",
  },
  "kit-guide-final-cta": {
    load: () =>
      import("@/components/kit/KitGuideFinalCta/KitGuideFinalCta").then((m) => m.KitGuideFinalCta),
    moduleId: "src/components/kit/KitGuideFinalCta/KitGuideFinalCta.tsx",
  },
  "kit-home-hero": {
    load: () => import("@/components/kit/KitHomeHero/KitHomeHero").then((m) => m.KitHomeHero),
    moduleId: "src/components/kit/KitHomeHero/KitHomeHero.tsx",
  },
  "kit-home-pain": {
    load: () => import("@/components/kit/KitHomePain/KitHomePain").then((m) => m.KitHomePain),
    moduleId: "src/components/kit/KitHomePain/KitHomePain.tsx",
  },
  "kit-home-services": {
    load: () =>
      import("@/components/kit/KitHomeServices/KitHomeServices").then((m) => m.KitHomeServices),
    moduleId: "src/components/kit/KitHomeServices/KitHomeServices.tsx",
  },
  "kit-reviews": {
    load: () => import("@/components/kit/KitReviews/KitReviews").then((m) => m.KitReviews),
    moduleId: "src/components/kit/KitReviews/KitReviews.tsx",
  },
  "kit-market-stat": {
    load: () => import("@/components/kit/KitMarketStat/KitMarketStat").then((m) => m.KitMarketStat),
    moduleId: "src/components/kit/KitMarketStat/KitMarketStat.tsx",
  },
  "kit-error-teaser": {
    load: () =>
      import("@/components/kit/KitErrorTeaser/KitErrorTeaser").then((m) => m.KitErrorTeaser),
    moduleId: "src/components/kit/KitErrorTeaser/KitErrorTeaser.tsx",
  },
  "kit-offer-formats": {
    load: () =>
      import("@/components/kit/KitOfferFormats/KitOfferFormats").then((m) => m.KitOfferFormats),
    moduleId: "src/components/kit/KitOfferFormats/KitOfferFormats.tsx",
  },
  "kit-cross-sell": {
    load: () => import("@/components/kit/KitCrossSell/KitCrossSell").then((m) => m.KitCrossSell),
    moduleId: "src/components/kit/KitCrossSell/KitCrossSell.tsx",
  },
  "kit-hero-portrait": {
    load: () =>
      import("@/components/kit/KitHeroPortrait/KitHeroPortrait").then((m) => m.KitHeroPortrait),
    moduleId: "src/components/kit/KitHeroPortrait/KitHeroPortrait.tsx",
  },
  "kit-before-after": {
    load: () =>
      import("@/components/kit/KitBeforeAfter/KitBeforeAfter").then((m) => m.KitBeforeAfter),
    moduleId: "src/components/kit/KitBeforeAfter/KitBeforeAfter.tsx",
  },
  "kit-service-tiers": {
    load: () =>
      import("@/components/kit/KitServiceTiers/KitServiceTiers").then((m) => m.KitServiceTiers),
    moduleId: "src/components/kit/KitServiceTiers/KitServiceTiers.tsx",
  },
  "kit-checklist-island": {
    load: () =>
      import("@/components/kit/KitChecklistIsland/KitChecklistIsland").then(
        (m) => m.KitChecklistIsland,
      ),
    moduleId: "src/components/kit/KitChecklistIsland/KitChecklistIsland.tsx",
  },
  "kit-final-panel": {
    load: () => import("@/components/kit/KitFinalPanel/KitFinalPanel").then((m) => m.KitFinalPanel),
    moduleId: "src/components/kit/KitFinalPanel/KitFinalPanel.tsx",
  },
  "kit-gift-island": {
    load: () => import("@/components/kit/KitGiftIsland/KitGiftIsland").then((m) => m.KitGiftIsland),
    moduleId: "src/components/kit/KitGiftIsland/KitGiftIsland.tsx",
  },
  "kit-benefits": {
    load: () => import("@/components/kit/KitBenefits/KitBenefits").then((m) => m.KitBenefits),
    moduleId: "src/components/kit/KitBenefits/KitBenefits.tsx",
  },
  "kit-stats": {
    load: () => import("@/components/kit/KitStats/KitStats").then((m) => m.KitStats),
    moduleId: "src/components/kit/KitStats/KitStats.tsx",
  },
  "kit-countdown": {
    load: () => import("@/components/kit/KitCountdown/KitCountdown").then((m) => m.KitCountdown),
    moduleId: "src/components/kit/KitCountdown/KitCountdown.tsx",
  },
  "kit-sale-hero": {
    load: () => import("@/components/kit/KitSaleHero/KitSaleHero").then((m) => m.KitSaleHero),
    moduleId: "src/components/kit/KitSaleHero/KitSaleHero.tsx",
  },
  "kit-sale-tiers": {
    load: () => import("@/components/kit/KitSaleTiers/KitSaleTiers").then((m) => m.KitSaleTiers),
    moduleId: "src/components/kit/KitSaleTiers/KitSaleTiers.tsx",
  },
  "kit-sale-bonus": {
    load: () => import("@/components/kit/KitSaleBonus/KitSaleBonus").then((m) => m.KitSaleBonus),
    moduleId: "src/components/kit/KitSaleBonus/KitSaleBonus.tsx",
  },
  "kit-sale-leadmagnet": {
    load: () =>
      import("@/components/kit/KitSaleLeadMagnet/KitSaleLeadMagnet").then(
        (m) => m.KitSaleLeadMagnet,
      ),
    moduleId: "src/components/kit/KitSaleLeadMagnet/KitSaleLeadMagnet.tsx",
  },
  "kit-program": {
    load: () => import("@/components/kit/KitProgram/KitProgram").then((m) => m.KitProgram),
    moduleId: "src/components/kit/KitProgram/KitProgram.tsx",
  },
  "kit-process": {
    load: () => import("@/components/kit/KitProcess/KitProcess").then((m) => m.KitProcess),
    moduleId: "src/components/kit/KitProcess/KitProcess.tsx",
  },
  "kit-case": {
    load: () => import("@/components/kit/KitCase/KitCase").then((m) => m.KitCase),
    moduleId: "src/components/kit/KitCase/KitCase.tsx",
  },
  "kit-links": {
    load: () => import("@/components/kit/KitLinks/KitLinks").then((m) => m.KitLinks),
    moduleId: "src/components/kit/KitLinks/KitLinks.tsx",
  },
};

const resolved = new Map<string, SectionComponent>();

export function isLazySectionType(type: string): boolean {
  return type in SECTIONS;
}

export function getResolvedSectionComponent(type: string): SectionComponent | undefined {
  return resolved.get(type);
}

// The stable assets.json key for a code-split section type (or undefined for a
// non-lazy type). Used by the SSR head's per-page CSS hoist (renderer/page-css.ts).
export function getSectionModuleId(type: string): string | undefined {
  return SECTIONS[type]?.moduleId;
}

// Exposed for the lockstep regression test (type ↔ loader ↔ moduleId parity).
export const LAZY_SECTION_TYPES: readonly string[] = Object.keys(SECTIONS);

export async function preloadSectionComponents(types: readonly string[]): Promise<void> {
  await Promise.all(
    types.map(async (type) => {
      const def = SECTIONS[type];
      if (!def || resolved.has(type)) return;
      resolved.set(type, await def.load());
    }),
  );
}

// Walk a page config's top-level sections and return the lazy section types that
// must be preloaded before render/hydrate. Accepts the raw config (unknown —
// shape narrowed defensively) so both renderer hooks can call it without
// importing the Zod-inferred type.
export function extractLazySectionTypes(config: unknown): string[] {
  if (!config || typeof config !== "object") return [];
  const sections = (config as { sections?: unknown }).sections;
  if (!Array.isArray(sections)) return [];
  const types = new Set<string>();
  for (const section of sections) {
    if (section && typeof section === "object") {
      const type = (section as { type?: unknown }).type;
      if (typeof type === "string" && isLazySectionType(type)) {
        types.add(type);
      }
    }
  }
  return [...types];
}
